import React, { FC, useState, useRef, useCallback } from 'react';
import { Input, Button, Spin, notification } from 'antd';
import {
  LoadingOutlined,
  DeleteFilled,
  PaperClipOutlined,
} from '@ant-design/icons';
import GraphemeSplitter from 'grapheme-splitter';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { IChatDetails } from '@/shared/interfaces/chat-details.interface';
import {
  ATTACHMENT_FILE_SIZE,
  ATTACHMENT_FILE_TYPES,
} from '@/shared/constants/fileTypes';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import EmojiIcon from '@/public/icons/emoji/EmojiIcon';
import useOutsideClick from '@/components/../hooks/useOutSideClick';
import {
  EMOJI_PICKER_CATEGORIES,
  UNSUPPORTED_BROWSER_EMOJI,
} from '@/pages/ChatDetails/config';

const splitter = new GraphemeSplitter();
const { TextArea } = Input;

const antIcon = <LoadingOutlined spin />;
const MAX_TEXTAREA_LENGTH = 500;

type Props = {
  chat: IChatDetails;
  sendMessage: (value: string) => Promise<void>;
  refreshChat: () => Promise<void>;
  closeChat: () => Promise<void>;
  endChat: () => Promise<void>;
  onChatTransfer: () => void;
};

export const ResponseField: FC<Props> = ({
  chat,
  sendMessage,
  refreshChat,
  closeChat,
  endChat,
  onChatTransfer,
}) => {
  const [notificationApi, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isChatEnded, setIsChatEnded] = useState(chat.state === 'Finished');
  const [resp, setResp] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerContainerRef = useRef(null);

  const isCurrentChatClosed = chat.state === 'Stopped';
  const conversationStatus = isChatEnded ? 'Close' : 'End';

  useOutsideClick(emojiPickerContainerRef, () => setIsOpen(false));

  const emojiClickHandler = useCallback(
    (emojiData: EmojiClickData) => {
      const graphemes = splitter.splitGraphemes(resp).length;
      if (graphemes < MAX_TEXTAREA_LENGTH) {
        setResp((prev) => prev + emojiData.emoji);
      }
      setIsOpen(false);
    },
    [resp],
  );

  const toggleEmojiPicker = () => {
    if (!isChatEnded && !selectedFile) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleAttachmentIconClick = () => {
    if (fileInputRef.current && !resp.trim()) {
      fileInputRef.current.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const graphemes = splitter.splitGraphemes(e.target.value);
    if (graphemes.length > MAX_TEXTAREA_LENGTH) {
      setResp(graphemes.slice(0, MAX_TEXTAREA_LENGTH).join(''));
    } else {
      setResp(e.target.value);
    }
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && e.ctrlKey) {
      submitResponse();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    if (file.size > ATTACHMENT_FILE_SIZE) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      notificationApi.error({
        message: 'Selected file is too large (maximum 20MB).',
        placement: 'bottomRight',
        duration: 5,
        className: 'notifications',
      });
      return;
    }

    setSelectedFile(file);
    setResp('');
    setError('');
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSelectedFile(null);
    setUploadError('');
  };

  const onFileUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError('');
    try {
      await SolgariApiService.putUploadFile(chat.chatId, selectedFile);
      setSelectedFile(null);
    } catch {
      setUploadError('Failed to upload file.');
    } finally {
      await refreshChat();
      setUploading(false);
    }
  };

  const submitResponse = async () => {
    if (selectedFile) {
      await onFileUpload();
    } else if (resp.trim()) {
      await sendMessage(resp);
      setResp('');
    }
  };

  const endConversation = () => {
    if (conversationStatus === 'Close') {
      closeConversation();
    } else {
      finishConversation();
    }
  };

  const finishConversation = async () => {
    setIsLoading(true);
    await endChat();
    setResp('');
    setIsChatEnded(true);
    setIsLoading(false);
  };

  const closeConversation = async () => {
    setIsLoading(true);
    await closeChat();
    setResp('');
    setIsLoading(false);
  };

  const customFormatter = ({
    maxLength = MAX_TEXTAREA_LENGTH,
  }: {
    maxLength?: number;
  }) => {
    const graphemes = splitter.splitGraphemes(resp).length;
    return `${graphemes}/${maxLength}`;
  };

  return (
    <>
      {contextHolder}
      {isLoading || uploading ? (
        <Spin className="spinner" indicator={antIcon} />
      ) : (
        <>
          <TextArea
            rows={4}
            showCount={{ formatter: customFormatter }}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className="textarea tw-w-full tw-resize-none"
            style={{ resize: 'none' }}
            value={resp}
            disabled={
              isChatEnded || isLoading || isCurrentChatClosed || !!selectedFile
            }
          />
          {error && <div className="error-msg">*{error}</div>}

          <div
            className={`new-message-actions-container ${selectedFile ? 'attachment-selected' : ''}`}
          >
            <div ref={emojiPickerContainerRef}>
              <EmojiPicker
                className="emoji-picker-position"
                hiddenEmojis={UNSUPPORTED_BROWSER_EMOJI}
                open={isOpen}
                width={240}
                height={390}
                categories={EMOJI_PICKER_CATEGORIES}
                onEmojiClick={emojiClickHandler}
              />
              <div
                className={`emoji-icon ${isChatEnded || selectedFile ? 'disabled-icon' : ''}`}
                onClick={toggleEmojiPicker}
              >
                <EmojiIcon />
              </div>
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_FILE_TYPES}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {!selectedFile && (
                <div
                  className={`attachment-button ${resp || isChatEnded || isLoading || isCurrentChatClosed ? 'disabled-icon' : ''}`}
                  onClick={handleAttachmentIconClick}
                >
                  <PaperClipOutlined />
                </div>
              )}

              {selectedFile && (
                <div className="attachment-actions">
                  <span className="attachment-name">{selectedFile.name}</span>
                  <Button
                    danger
                    shape="circle"
                    data-testid="remove-file"
                    size="small"
                    icon={<DeleteFilled />}
                    onClick={handleRemoveFile}
                  />
                </div>
              )}

              {uploadError && <div className="error-msg">*{uploadError}</div>}
            </div>
          </div>

          <div className="actions">
            <Button
              onClick={endConversation}
              data-testid="end-conversation"
              disabled={isCurrentChatClosed}
            >
              {conversationStatus}
            </Button>
            <Button
              data-testid="chat-transfer"
              onClick={onChatTransfer}
              disabled={isCurrentChatClosed}
            >
              Transfer
            </Button>
            <Button
              data-testid="submit-response"
              onClick={submitResponse}
              disabled={
                isChatEnded ||
                isLoading ||
                isCurrentChatClosed ||
                (!resp.trim() && !selectedFile)
              }
            >
              Send
            </Button>
          </div>
        </>
      )}
    </>
  );
};
